import React from 'react';
import {
  Search,
  ArrowUpDown,
  Plus,
  RefreshCw,
  X,
  Check,
  CalendarDays,
  Trophy,
  Compass,
  Bell,
  Download,
  Library,
} from 'lucide-react';
import { BrandLogo } from './BrandLogo';

export type ActiveViewMode = 'tier' | 'schedule' | 'explorer';

interface HeaderProps {
  activeView: ActiveViewMode;
  onChangeView: (view: ActiveViewMode) => void;
  searchQuery: string;
  onSearchChange: (q: string) => void;
  showSearchBar: boolean;
  onToggleSearchBar: () => void;
  isSortMode: boolean;
  onToggleSortMode: () => void;
  onOpenAddModal: () => void;
  onOpenLineModal: () => void;
  onCheckUpdates: () => void;
  isCheckingUpdates: boolean;
  airingTodayCount: number;
  lineNotifyCount: number;
  onExportBackup: () => void;
  onOpenLibrary: () => void;
}

const NAV_ITEMS: Array<{ id: ActiveViewMode; label: string; icon: typeof Trophy }> = [
  { id: 'tier', label: 'จัดอันดับ', icon: Trophy },
  { id: 'schedule', label: 'ตารางฉาย', icon: CalendarDays },
  { id: 'explorer', label: 'สำรวจ', icon: Compass },
];

const iconBtn =
  'relative h-8 w-8 sm:h-9 sm:w-auto sm:px-3 rounded-xl text-sm font-medium flex items-center justify-center gap-1.5 transition-all active:scale-95 shrink-0';
const ghostBtn = `${iconBtn} text-slate-300 hover:text-white bg-white/[0.04] hover:bg-white/[0.09] border border-white/[0.07]`;

export const Header: React.FC<HeaderProps> = ({
  activeView,
  onChangeView,
  searchQuery,
  onSearchChange,
  showSearchBar,
  onToggleSearchBar,
  isSortMode,
  onToggleSortMode,
  onOpenAddModal,
  onOpenLineModal,
  onCheckUpdates,
  isCheckingUpdates,
  airingTodayCount,
  lineNotifyCount,
  onExportBackup,
  onOpenLibrary,
}) => {
  return (
    <>
      <header className="sticky top-0 z-40 safe-area-top bg-[#070914]/90 backdrop-blur-md border-b border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 h-14 sm:h-16 flex items-center justify-between gap-2">
          {/* Brand */}
          <button
            type="button"
            onClick={() => onChangeView('tier')}
            className="flex items-center gap-2 sm:gap-2.5 min-w-0 shrink lg:shrink-0 group"
          >
            <BrandLogo className="w-8 h-8 sm:w-9 sm:h-9 shrink-0 group-hover:scale-105 transition-transform" />
            <div className="hidden min-[380px]:block text-left leading-tight min-w-0">
              <div className="text-[15px] sm:text-lg font-bold tracking-tight text-white whitespace-nowrap">
                Anime<span className="text-gradient-brand"> Ranker</span>
              </div>
              <div className="hidden sm:block text-[11px] text-slate-500 whitespace-nowrap">
                จัดอันดับ · ตารางฉาย · แจ้งเตือน
              </div>
            </div>
          </button>

          {/* Desktop navigation */}
          <nav className="hidden lg:flex shrink-0 items-center p-1 rounded-2xl bg-white/[0.03] border border-white/[0.06]">
            {NAV_ITEMS.map(({ id, label, icon: Icon }) => {
              const active = activeView === id;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => onChangeView(id)}
                  className={`relative px-4 py-1.5 rounded-xl text-sm font-medium flex items-center gap-2 whitespace-nowrap transition-all ${
                    active
                      ? 'bg-white/[0.1] text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]'
                      : 'text-slate-400 hover:text-slate-100'
                  }`}
                >
                  <Icon className={`w-4 h-4 ${active ? 'text-fuchsia-300' : ''}`} />
                  <span>{label}</span>
                  {id === 'schedule' && airingTodayCount > 0 && (
                    <span className="min-w-[18px] h-[18px] px-1 grid place-items-center bg-rose-500 text-white text-[10px] font-bold rounded-full">
                      {airingTodayCount}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>

          {/* Actions */}
          <div className="flex items-center gap-1 sm:gap-1.5 shrink-0">
            <button
              type="button"
              onClick={() => {
                if (showSearchBar && searchQuery) {
                  onSearchChange('');
                } else {
                  onToggleSearchBar();
                }
              }}
              title="ค้นหาอนิเมะ"
              className={
                showSearchBar || searchQuery
                  ? `${iconBtn} bg-sky-500/20 text-sky-200 border border-sky-400/40`
                  : ghostBtn
              }
            >
              <Search className="w-4 h-4" />
              <span className="hidden xl:inline">ค้นหา</span>
            </button>

            {activeView === 'tier' && (
              <button
                type="button"
                onClick={onToggleSortMode}
                title={isSortMode ? 'บันทึกการจัดเรียง' : 'จัดเรียงการ์ด (ลากวาง)'}
                className={
                  isSortMode
                    ? `${iconBtn} sm:px-3 bg-emerald-500 text-white ring-2 ring-emerald-300/40`
                    : ghostBtn
                }
              >
                {isSortMode ? <Check className="w-4 h-4" /> : <ArrowUpDown className="w-4 h-4" />}
                <span className={isSortMode ? 'hidden sm:inline' : 'hidden xl:inline'}>
                  {isSortMode ? 'เสร็จสิ้น' : 'จัดเรียง'}
                </span>
              </button>
            )}

            <button
              type="button"
              onClick={onCheckUpdates}
              disabled={isCheckingUpdates}
              title="เช็คตอนใหม่จาก Bilibili, Anime-Waku และ YouTube"
              className={`${ghostBtn} disabled:opacity-60`}
            >
              <RefreshCw className={`w-4 h-4 ${isCheckingUpdates ? 'animate-spin text-teal-300' : ''}`} />
              <span className="hidden xl:inline">{isCheckingUpdates ? 'กำลังเช็ค…' : 'เช็คตอนใหม่'}</span>
            </button>

            <button
              type="button"
              onClick={onOpenLibrary}
              title="คลังดาวน์โหลด — ตอนที่โหลดเก็บไว้ ดูในแอปได้"
              className={ghostBtn}
            >
              <Library className="w-4 h-4 text-emerald-300" />
            </button>

            <button
              type="button"
              onClick={onOpenLineModal}
              title="ตั้งค่าแจ้งเตือน LINE"
              className={ghostBtn}
            >
              <Bell className="w-4 h-4 text-[#4ef08f]" />
              <span className="hidden xl:inline">LINE</span>
              {lineNotifyCount > 0 && (
                <span className="absolute -top-1 -right-1 sm:static min-w-[16px] h-4 px-1 grid place-items-center bg-[#06C755] text-slate-950 text-[9px] font-bold rounded-full">
                  {lineNotifyCount}
                </span>
              )}
            </button>

            <button
              type="button"
              onClick={onExportBackup}
              title="สำรองข้อมูลเป็นไฟล์ JSON"
              className={`${ghostBtn} hidden sm:flex`}
            >
              <Download className="w-4 h-4" />
            </button>

            <button
              type="button"
              onClick={onOpenAddModal}
              title="เพิ่มอนิเมะ"
              className="h-8 w-8 sm:h-9 sm:w-auto sm:px-4 ml-0.5 rounded-xl bg-gradient-to-br from-fuchsia-500 via-purple-500 to-indigo-500 text-white text-sm font-semibold flex items-center justify-center gap-1.5 shadow-lg shadow-fuchsia-600/25 hover:brightness-110 active:scale-95 transition-all shrink-0"
            >
              <Plus className="w-[18px] h-[18px]" strokeWidth={2.5} />
              <span className="hidden sm:inline">เพิ่มอนิเมะ</span>
            </button>
          </div>
        </div>

        {/* Expandable search bar */}
        {showSearchBar && (
          <div className="border-t border-white/[0.06] px-3 sm:px-6 py-2.5">
            <div className="max-w-lg mx-auto relative flex items-center">
              <Search className="absolute left-3.5 w-4 h-4 text-slate-500 pointer-events-none" />
              <input
                type="text"
                autoFocus
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder="ค้นหาชื่อเรื่อง (ไทย / อังกฤษ / โรมาจิ)"
                className="w-full bg-white/[0.04] border border-white/[0.08] rounded-2xl py-2.5 pl-10 pr-11 text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-fuchsia-500/40 focus:border-fuchsia-400/50 transition-all"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => onSearchChange('')}
                  title="ล้างคำค้นหา"
                  className="absolute right-2 w-7 h-7 rounded-full bg-white/[0.08] hover:bg-white/[0.14] text-slate-300 grid place-items-center transition-colors"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
        )}
      </header>

      {/* Mobile / tablet bottom tab bar */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-[#0a0c18]/95 backdrop-blur-md border-t border-white/[0.07] safe-area-bottom">
        <div className="max-w-md mx-auto grid grid-cols-3 px-2 pt-1.5">
          {NAV_ITEMS.map(({ id, label, icon: Icon }) => {
            const active = activeView === id;
            return (
              <button
                key={id}
                type="button"
                onClick={() => onChangeView(id)}
                className="relative flex flex-col items-center gap-0.5 py-1 active:scale-95 transition-transform"
              >
                <span
                  className={`relative w-12 h-7 rounded-full grid place-items-center transition-colors ${
                    active ? 'bg-fuchsia-500/20' : ''
                  }`}
                >
                  <Icon
                    className={`w-[19px] h-[19px] ${active ? 'text-fuchsia-200' : 'text-slate-500'}`}
                    strokeWidth={active ? 2.4 : 2}
                  />
                  {id === 'schedule' && airingTodayCount > 0 && (
                    <span className="absolute -top-0.5 right-1.5 min-w-[16px] h-4 px-1 grid place-items-center bg-rose-500 text-white text-[9px] font-bold rounded-full ring-2 ring-[#0a0c18]">
                      {airingTodayCount}
                    </span>
                  )}
                </span>
                <span
                  className={`text-[11px] font-medium ${active ? 'text-white' : 'text-slate-500'}`}
                >
                  {label}
                </span>
              </button>
            );
          })}
        </div>
      </nav>
    </>
  );
};
